import React from 'react';
import {scope, locales} from '../settings';
import {
  logger,
  getDisplayItemTextPhraseFromSlug,
  assetImgUrl
} from '../helperFunctions';
import {getSessionStorageField, setSessionStorageField} from './contextHelper';
import {
  fetchContent,
  refreshAccessToken,
  loginUser as loginUserAPI,
  registerAccount as registerAccountAPI,
  forgotPassword as forgotPasswordAPI,
  resetPassword as resetPasswordAPI,
  verifyEmail as verifyEmailAPI,
  resendVerificationEmail as resendVerificationEmailAPI
} from '../Api';

const SPANISH_LOCALE = 'es';
const ENGLISH_LOCALE = 'en';

const getErrObject = err => {
  if (err?.[0]?.[0]?.toString() === '{') {
    return JSON.parse(err[0]).error;
  }
  if (err?.[0]?.error) {
    return err[0].error;
  }
  if (err?.error) {
    return err.error;
  }
  return null;
};

const getErrSlug = (err, defaultString) => {
  if (typeof err === 'string' || err instanceof String) {
    return err;
  }
  return getErrObject(err)?.slug || defaultString;
};

const getErrVars = err => {
  const {retryAfterMinutes} = getErrObject(err) || {};
  return retryAfterMinutes ? {minutes: retryAfterMinutes} : undefined;
};

const useAuthAndContent = ({setLoading}) => {
  const [content, setContent] = React.useState(null);
  const [locale, setLocale] = React.useState(locales[0]);

  const [snackBarMessage, setSnackBarMessage] = React.useState('');

  const [token, setToken] = React.useState(
    getSessionStorageField('token', null)
  );

  // CONTENT HOOKS ===================================================

  React.useEffect(() => {
    const faviconUrl = assetImgUrl(content, 'favicon');

    const faviconElement = document.querySelector("link[rel~='icon']");

    if (faviconUrl && faviconElement) {
      faviconElement.href = faviconUrl;
    }
  }, [content]);

  React.useEffect(() => {
    if (!scope) {
      return;
    }
    fetchContent(scope, token?.accessToken).then(ret => {
      if (!ret || ret.errors) {
        return logger(`fetchContent failed: ${JSON.stringify(ret)}`);
      }
      setContent(ret);

      // Apply dynamic theme colors
      const colors = ret?.config?.riderApp?.style?.colors;
      if (colors) {
        const root = document.documentElement;
        Object.entries(colors).forEach(([key, value]) => {
          root.style.setProperty(`--color-${key}`, value);
        });
      }

      return (window.content = ret);
    });
  }, [token?.accessToken]);

  const localeText = React.useCallback(
    (slug, placeHolder, vars) => {
      const text = content?.locales?.[locale]?.[slug]?.trim();
      const enText = content?.locales?.[ENGLISH_LOCALE]?.[slug]?.trim();

      let availableText = text || enText || placeHolder || slug;
      if (availableText && vars) {
        availableText = Object.keys(vars).reduce(
          (acc, key) => acc.replace(`{${key}}`, vars[key]),
          availableText
        );
      }
      if (availableText) {
        if (!availableText.includes('\n')) {
          return availableText;
        }

        const availableTextLines = availableText.split('\n');
        return availableTextLines.map((line, i) => (
          <React.Fragment key={i}>
            {i !== 0 && <br />}
            {line}
          </React.Fragment>
        ));
      }

      logger('no slug nor placeholder provided');

      if (locale === SPANISH_LOCALE) {
        return 'error de traducción';
      }
      return 'translation failure';
    },
    [content, locale]
  );

  const getTicketName = React.useCallback(
    ticketId => {
      const product = content?.catalog?.products?.find(
        product => product.id.toString() === ticketId.toString()
      )?.properties;
      const {riderType, fareType, serviceType, zone} = product || {};

      return [riderType, fareType, serviceType, zone]
        .filter(slug => slug != null)
        .map(slug =>
          getDisplayItemTextPhraseFromSlug(localeText, content, slug)
        )
        .join(' ');
    },
    [content, localeText]
  );

  // DISPLAY HOOKS =================================================

  const showError = React.useCallback(
    (err, defaultString) =>
      setSnackBarMessage(
        localeText(getErrSlug(err, defaultString), defaultString, getErrVars(err))
      ),
    [localeText, setSnackBarMessage]
  );

  // AUTH HOOKS ===================================================

  const getValidToken = React.useCallback(
    async () =>
      refreshAccessToken(scope, token).then(newToken => {
        if (newToken.accessToken !== token.accessToken) {
          setToken(newToken);
        }
        return newToken;
      }),
    [token]
  );

  const saveToken = React.useCallback(
    userToken => {
      setSessionStorageField('token', userToken);
      setToken(userToken);
    },
    [setToken]
  );

  const loginUser = React.useCallback(
    async credentials => {
      setLoading(true);
      return loginUserAPI(scope, credentials).then(res => {
        setLoading(false);
        if (res.errors) {
          logger('loginUserAPI failed');
          showError(res.errors);
          return res;
        }

        saveToken(res.data.oauthToken);
        return res;
      });
    },
    [showError, setLoading, saveToken]
  );

  const signOut = () => {
    setSessionStorageField('token', null);
    setToken(null);
  };

  const registerAccount = React.useCallback(
    async newAccount => {
      setLoading(true);
      return registerAccountAPI(scope, newAccount).then(ret => {
        setLoading(false);
        if (ret?.errors) {
          logger('registerAccountAPI failed');
          logger(ret);
          showError(ret.errors, 'Unknown Failure');
          return ret;
        }

        setSnackBarMessage(
          localeText(
            'rw_register_msg_acc_successefully_created',
            'Account Successfully created'
          )
        );

        return ret;
      });
    },
    [showError, setLoading, localeText, setSnackBarMessage]
  );

  const forgotPassword = React.useCallback(
    async email => {
      setLoading(true);
      return forgotPasswordAPI(scope, email).then(ret => {
        setLoading(false);
      });
    },
    [setLoading]
  );

  const resetPassword = React.useCallback(
    async (token, password) => {
      setLoading(true);
      return resetPasswordAPI(token, scope, password).then(ret => {
        setLoading(false);
        return ret;
      });
    },
    [setLoading]
  );

  const verifyEmail = React.useCallback(
    async (token, verificationScope) => {
      setLoading(true);
      return verifyEmailAPI(token, verificationScope).then(ret => {
        setLoading(false);
        return ret;
      });
    },
    [setLoading]
  );

  const resendVerificationEmail = React.useCallback(
    async emailAddress => {
      setLoading(true);
      return resendVerificationEmailAPI(scope, emailAddress).then(ret => {
        setLoading(false);
        return ret;
      });
    },
    [setLoading]
  );

  return {
    content,
    locale,
    setLocale,
    localeText,
    getTicketName,
    snackBarMessage,
    setSnackBarMessage,
    showError,
    token,
    saveToken,
    getValidToken,
    loginUser,
    signOut,
    registerAccount,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail
  };
};

export default useAuthAndContent;
